import { useEffect, useState } from 'react';
import api from '../services/api';
import Button from '../components/Button';
import Input from '../components/Input';
import Select from '../components/Select';
import LookupSelect, { LookupOption } from '../components/LookupSelect';
import Table from '../components/Table';
import StatusBadge from '../components/StatusBadge';

const fetchEmployeeOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get('/employees');
  const employees = res.data.employees || [];
  const filtered = search ? employees.filter((e: any) => e.full_name.toLowerCase().includes(search.toLowerCase())) : employees;
  return filtered.slice(0, 20).map((e: any) => ({ value: e.id, label: `${e.full_name} (${e.code})` }));
};

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function FwaApplications() {
  const [applications, setApplications] = useState<any[]>([]);
  const [pending, setPending] = useState<any[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    employeeId: '', arrangementType: 'FLEXI_PLACE', location: '', fromDate: '', toDate: '',
    isHalfDay: false, weekdayPattern: [] as number[], reason: '',
  });

  const load = async () => {
    const [appsRes, pendingRes] = await Promise.all([
      api.get('/lms/fwa/applications'),
      api.get('/lms/fwa/applications/my-approvals'),
    ]);
    setApplications(appsRes.data.applications || []);
    setPending(pendingRes.data.approvals || []);
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/lms/fwa/applications', { ...form, weekdayPattern: form.weekdayPattern.length ? form.weekdayPattern : null });
      setShowForm(false);
      setForm({ employeeId: '', arrangementType: 'FLEXI_PLACE', location: '', fromDate: '', toDate: '', isHalfDay: false, weekdayPattern: [], reason: '' });
      load();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to submit');
    }
  };

  const act = async (id: string, action: 'APPROVED' | 'REJECTED') => {
    try {
      await api.put(`/lms/fwa/applications/${id}/approve`, { action });
      load();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to act');
    }
  };

  const toggleWeekday = (d: number) => {
    setForm((f) => ({ ...f, weekdayPattern: f.weekdayPattern.includes(d) ? f.weekdayPattern.filter((x) => x !== d) : [...f.weekdayPattern, d] }));
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Flexible Work Arrangement</h1>
        <Button onClick={() => setShowForm(!showForm)}>{showForm ? 'Cancel' : 'Apply for FWA'}</Button>
      </div>

      {pending.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <h2 className="font-semibold text-amber-900 mb-3">Pending FWA Approvals ({pending.length})</h2>
          <ul className="space-y-2">
            {pending.map((p: any) => (
              <li key={p.approvalInstanceId} className="flex justify-between items-center bg-white rounded-lg p-3 text-sm">
                <span>
                  {p.first_name} {p.last_name} ({p.employee_code}) — {p.arrangement_type} {p.location ? `@ ${p.location}` : ''},{' '}
                  {new Date(p.from_date).toLocaleDateString()} to {new Date(p.to_date).toLocaleDateString()} — level {p.currentLevel}/{p.finalLevel}
                </span>
                <div className="flex gap-2">
                  <button onClick={() => act(p.approvalInstanceId, 'APPROVED')} className="px-3 py-1 bg-green-600 text-white rounded text-xs">Approve</button>
                  <button onClick={() => act(p.approvalInstanceId, 'REJECTED')} className="px-3 py-1 bg-red-600 text-white rounded text-xs">Reject</button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {showForm && (
        <form onSubmit={submit} className="bg-white rounded-lg shadow p-6 mb-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <LookupSelect label="Employee" value={form.employeeId} onChange={(v) => setForm({ ...form, employeeId: v })} fetchOptions={fetchEmployeeOptions} required />
            <Select label="Arrangement Type" value={form.arrangementType} onChange={(e) => setForm({ ...form, arrangementType: e.target.value })}
              options={[{ value: 'FLEXI_PLACE', label: 'Flexi-Place' }, { value: 'FLEXI_TIME', label: 'Flexi-Time' }]} />
            {form.arrangementType === 'FLEXI_PLACE' && (
              <Input label="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
            )}
            <Input label="From Date" type="date" value={form.fromDate} onChange={(e) => setForm({ ...form, fromDate: e.target.value })} required />
            <Input label="To Date" type="date" value={form.toDate} onChange={(e) => setForm({ ...form, toDate: e.target.value })} required />
            <label className="flex items-center gap-2 text-sm mt-6">
              <input type="checkbox" checked={form.isHalfDay} onChange={(e) => setForm({ ...form, isHalfDay: e.target.checked })} /> Half Day
            </label>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Every (specific weekdays, optional)</label>
            <div className="flex gap-2 mt-1">
              {WEEKDAYS.map((d, idx) => (
                <button type="button" key={idx} onClick={() => toggleWeekday(idx)}
                  className={`px-2 py-1 rounded text-xs ${form.weekdayPattern.includes(idx) ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}>
                  {d}
                </button>
              ))}
            </div>
          </div>
          <Input label="Reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          <Button type="submit">Submit FWA Application</Button>
        </form>
      )}

      <Table
        columns={[
          { key: 'application_number', header: 'App No.' },
          { key: 'employee', header: 'Employee', render: (r: any) => `${r.first_name} ${r.last_name} (${r.employee_code})` },
          { key: 'arrangement_type', header: 'Type' },
          { key: 'location', header: 'Location', render: (r: any) => r.location || '—' },
          { key: 'from_date', header: 'From', render: (r: any) => new Date(r.from_date).toLocaleDateString() },
          { key: 'to_date', header: 'To', render: (r: any) => new Date(r.to_date).toLocaleDateString() },
          { key: 'status', header: 'Status', render: (r: any) => <StatusBadge status={r.status} /> },
        ]}
        rows={applications}
      />
    </div>
  );
}
